import type { ExecutionRow, RuleInput, RuleSummary } from '../shared/contracts';

export interface Bootstrap {
  tenant: { public_id: string; subdomain: string; name: string; status: string; action_builder_verified: boolean };
  rules: RuleSummary[];
  credential: { configured: boolean; rotated_at?: string };
  readiness: {
    connected: boolean;
    admin_verified: boolean;
    active_rule_count: number;
    credential_configured: boolean;
    applied_execution_count: number;
    action_builder_verified: boolean;
    complete: boolean;
  };
  endpoint: string;
  executions: ExecutionRow[];
  execution_total: number;
  admin: { name: string; zendesk_user_id: number };
  connection: { state: string; access_expires_at: string | null; refresh_expires_at: string | null; health_verified: boolean };
  integration: { mode: 'action_builder' | 'webhook'; provisioned: boolean; ingress_enabled: boolean; webhook_id: string | null; evidence: Array<{ rule_key: string; execution_id: string | null }> };
  metrics: { total: number; applied: number; created_users: number; attention: number; held: number; pending: number; timezone: string; since: string };
  updated_at: string;
}

function cookie(name: string): string {
  return document.cookie.split('; ').find((part) => part.startsWith(`${name}=`))?.split('=').slice(1).join('=') ?? '';
}

export class ApiError extends Error {
  constructor(message: string, public status: number, public code: string) { super(message); }
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    signal: AbortSignal.timeout(20_000),
    ...init,
    credentials: 'same-origin',
    headers: {
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init.method && !['GET', 'HEAD'].includes(init.method) ? { 'X-CSRF-Token': decodeURIComponent(cookie(path.startsWith('/ops/') ? 'rc_ops_csrf' : 'rc_csrf')) } : {}),
      ...init.headers,
    },
  });
  if (!response.ok) {
    const value: unknown = await response.json().catch(() => null);
    const message = typeof value === 'object' && value !== null && 'message' in value && typeof value.message === 'string'
      ? value.message
      : `HTTP ${response.status}`;
    const code = typeof value === 'object' && value !== null && 'error' in value ? String(value.error) : 'http_error';
    throw new ApiError(message, response.status, code);
  }
  if (response.status === 204) return undefined as T;
  return response.json();
}

export const loadBootstrap = () => api<Bootstrap>('/api/bootstrap');
export const createRule = (rule: RuleInput) => api<{ rule: RuleSummary }>('/api/rules', { method: 'POST', body: JSON.stringify(rule) });
export const updateRule = (key: string, rule: RuleInput) => api<{ rule: RuleSummary }>(`/api/rules/${key}`, { method: 'PUT', body: JSON.stringify(rule) });
export const deleteRule = (key: string) => api<void>(`/api/rules/${key}`, { method: 'DELETE' });
export const duplicateRule = (key: string) => api<{ rule: RuleSummary }>(`/api/rules/${key}/duplicate`, { method: 'POST' });
export const rotateCredential = () => api<{ secret: string; rotated_at: string; notice: string }>('/api/action-credential/rotate', { method: 'POST' });
export const testLiveTicket = (ruleKey: string, ticketId: number) => api<{ status: string; requester_id?: number; masked_email?: string; masked_phone?: string; name?: string; message?: string }>(`/api/rules/${ruleKey}/test-ticket`, { method: 'POST', body: JSON.stringify({ ticket_id: ticketId }) });
export const verifyActionBuilder = (ruleKey: string, ticketId: number, executionId: string) => api<{ verified: true; execution_id: string }>('/api/onboarding/verify-action-builder', { method: 'POST', body: JSON.stringify({ rule_key: ruleKey, ticket_id: ticketId, execution_id: executionId }) });

export const loadExecutions = (offset = 0, category = 'all', ticketId = '') => api<{ executions: ExecutionRow[]; total: number; offset: number; limit: number; updated_at: string }>(`/api/executions?offset=${offset}&category=${encodeURIComponent(category)}${ticketId ? `&ticket_id=${encodeURIComponent(ticketId)}` : ''}`);
export const messageOf = (reason: unknown) => reason instanceof Error ? reason.message : String(reason);
export const statusLabel = (status: string) => status.replaceAll('_', ' ').replace(/^./u, letter => letter.toUpperCase());
export const connectionLabel = (state: string) => ({ credentials_stored: 'Credentials stored', renewal_pending: 'Token renewal pending', suspended: 'Tenant suspended', reauthorization_required: 'Reconnect required', not_connected: 'Not connected' })[state] ?? statusLabel(state);
